/**
 * CaseList - Felles komponent for visning av saker/case oversikt
 * Gjenbrukbar på tvers av alle moduler
 * @version 1.0.0 - MASTERPROMPT v2.0 compliant (Fase 3 - Deduplisering)
 */

import React, { useState, useMemo } from 'react';
import { 
  Plus, Search, Clock, CheckCircle, Trophy, XCircle, 
  ArrowUpCircle, Edit3, Trash2, Calendar, ChevronRight,
  FolderOpen, Filter, Package
} from 'lucide-react';

// Status configurations
const STATUS_CONFIG = {
  draft: { icon: Edit3, color: 'text-gray-400 bg-gray-500/10', label: 'Utkast' },
  utkast: { icon: Edit3, color: 'text-gray-400 bg-gray-500/10', label: 'Utkast' },
  pågående: { icon: Clock, color: 'text-blue-400 bg-blue-500/10', label: 'Pågående' },
  venter_svar: { icon: Clock, color: 'text-yellow-400 bg-yellow-500/10', label: 'Venter svar' },
  avsluttet: { icon: CheckCircle, color: 'text-gray-400 bg-gray-500/10', label: 'Avsluttet' },
  vunnet: { icon: Trophy, color: 'text-[var(--success)] bg-[var(--success)]/10', label: 'Vunnet' },
  tapt: { icon: XCircle, color: 'text-red-400 bg-red-500/10', label: 'Tapt' },
  eskalert: { icon: ArrowUpCircle, color: 'text-purple-400 bg-purple-500/10', label: 'Eskalert' }
};

// Category icons (can be extended per module)
const CATEGORY_ICONS = {
  kontrakt: Package,
  oppsigelse: XCircle,
  permittering: Clock,
  analyse: Search,
  reklamasjon: Package,
  default: FolderOpen
};

export function CaseList({
  cases = [],
  loading = false,
  onSelectCase,
  onNewCase,
  onDeleteCase,
  
  // Customization
  newCaseLabel = 'Ny sak',
  emptyTitle = 'Ingen saker funnet',
  emptyDescription = 'Start med å opprette din første sak',
  searchPlaceholder = 'Søk i saker...',
  
  // Module-specific
  getCaseTitle = (c) => c.navn || c.produktNavn || c.dirName || 'Uten navn',
  getCaseSubtitle = (c) => c.selgerNavn || c.type || '',
  getCaseCategory = (c) => c.type || 'default',
  getCaseStatus = (c) => c.status || 'draft',
  getCaseDate = (c) => c.savedAt || c.createdAt
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('alle');
  
  // Filter and sort cases
  const filteredCases = useMemo(() => {
    return cases
      .filter(c => {
        const title = getCaseTitle(c).toLowerCase();
        const subtitle = getCaseSubtitle(c).toLowerCase();
        const matchesSearch = !searchQuery || 
          title.includes(searchQuery.toLowerCase()) ||
          subtitle.includes(searchQuery.toLowerCase());
        
        const status = getCaseStatus(c);
        const matchesStatus = statusFilter === 'alle' || status === statusFilter;
        
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => new Date(getCaseDate(b) || 0) - new Date(getCaseDate(a) || 0));
  }, [cases, searchQuery, statusFilter, getCaseTitle, getCaseSubtitle, getCaseStatus, getCaseDate]);
  
  // Available statuses from current cases
  const availableStatuses = useMemo(() => {
    const statuses = new Set(cases.map(c => getCaseStatus(c)));
    return Array.from(statuses).filter(s => STATUS_CONFIG[s]);
  }, [cases, getCaseStatus]);
  
  return (
    <div className="space-y-6" data-testid="case-list">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder={searchPlaceholder}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-[var(--bg-highlight)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-main)] placeholder-[var(--text-muted)] focus:outline-none focus:border-[var(--copper)]"
            data-testid="case-search-input"
          />
        </div>
        
        <div className="flex gap-2">
          {availableStatuses.length > 1 && (
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-[var(--bg-highlight)] border border-[var(--border-subtle)] rounded-lg text-[var(--text-main)] text-sm focus:outline-none focus:border-[var(--copper)]"
              data-testid="status-filter"
            >
              <option value="alle">Alle statuser</option>
              {availableStatuses.map(status => (
                <option key={status} value={status}>
                  {STATUS_CONFIG[status]?.label || status}
                </option>
              ))}
            </select>
          )}
          
          <button
            onClick={onNewCase}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[var(--copper)] to-[var(--copper-dark)] rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
            data-testid="new-case-btn"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">{newCaseLabel}</span>
          </button>
        </div>
      </div>
      
      {/* Cases Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-40 bg-[var(--bg-highlight)] rounded-lg animate-pulse" />
          ))}
        </div>
      ) : filteredCases.length === 0 ? (
        <div className="text-center py-12 bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded-lg">
          <FolderOpen className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-4" />
          <h3 className="text-lg font-medium text-[var(--text-main)] mb-2">{emptyTitle}</h3>
          <p className="text-[var(--text-muted)] mb-4">
            {searchQuery || statusFilter !== 'alle' 
              ? 'Prøv å endre søk eller filter'
              : emptyDescription
            }
          </p>
          {!searchQuery && statusFilter === 'alle' && (
            <button
              onClick={onNewCase}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--copper)] rounded-lg text-white font-medium hover:opacity-90"
            >
              <Plus className="w-4 h-4" />
              {newCaseLabel}
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCases.map((caseItem, idx) => {
            const status = getCaseStatus(caseItem);
            const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
            const StatusIcon = statusConfig.icon;
            const category = getCaseCategory(caseItem);
            const CategoryIcon = CATEGORY_ICONS[category] || CATEGORY_ICONS.default;
            
            return (
              <div
                key={caseItem.dirName || caseItem.id || idx}
                onClick={() => onSelectCase(caseItem)}
                className="group p-4 bg-[var(--bg-card)] border border-[var(--border-subtle)] rounded-lg hover:border-[var(--copper)]/40 transition-all cursor-pointer"
                data-testid={`case-card-${idx}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <CategoryIcon className="w-4 h-4 text-[var(--copper)]" />
                    <h3 className="font-medium text-[var(--text-main)] truncate max-w-[180px]">
                      {getCaseTitle(caseItem)}
                    </h3>
                  </div>
                  <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${statusConfig.color}`}>
                    <StatusIcon className="w-3 h-3" />
                    {statusConfig.label}
                  </div>
                </div>
                
                {getCaseSubtitle(caseItem) && (
                  <p className="text-sm text-[var(--text-muted)] mb-2 truncate">
                    {getCaseSubtitle(caseItem)}
                  </p>
                )}
                
                {getCaseDate(caseItem) && (
                  <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                    <Calendar className="w-3 h-3" />
                    Sist endret: {new Date(getCaseDate(caseItem)).toLocaleDateString('nb-NO')}
                  </div>
                )}
                
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-[var(--border-subtle)]">
                  {onDeleteCase && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteCase(caseItem);
                      }}
                      className="p-1.5 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                      title="Slett sak"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                  
                  <div className="flex items-center gap-1 text-sm text-[var(--copper)] group-hover:translate-x-1 transition-transform ml-auto">
                    Åpne <ChevronRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default CaseList;
